import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Assistant, AssistantPermissions } from '@/types/entities';

/**
 * Hook to fetch all assistants for a restaurant
 */
export function useAssistants(restaurantId: string) {
  return useQuery({
    queryKey: ['assistants', restaurantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('assistants')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transform snake_case to camelCase
      return (data as any[]).map((assistant) => ({
        id: assistant.id,
        restaurantId: assistant.restaurant_id,
        username: assistant.username,
        email: assistant.email,
        roleName: assistant.role_name,
        permissions: assistant.permissions,
        isActive: assistant.is_active,
        languagePreference: assistant.language_preference,
        deletedAt: assistant.deleted_at,
        createdAt: assistant.created_at,
        updatedAt: assistant.updated_at,
      })) as Assistant[];
    },
    enabled: !!restaurantId,
  });
}

/**
 * Hook to create a new assistant
 */
export function useCreateAssistant(restaurantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      username: string;
      password: string;
      roleName: string;
      permissions: AssistantPermissions;
      languagePreference?: string;
    }) => {
      const { data: result, error } = await supabase.rpc('create_assistant', {
        p_restaurant_id: restaurantId,
        p_username: data.username,
        p_password: data.password,
        p_role_name: data.roleName,
        p_permissions: data.permissions as any,
        p_language_preference: data.languagePreference || 'ar',
      });

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assistants', restaurantId] });
    },
  });
}

/**
 * Hook to update an assistant
 */
export function useUpdateAssistant(restaurantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      assistantId: string;
      roleName: string;
      permissions: AssistantPermissions;
      isActive: boolean;
    }) => {
      const { data: result, error } = await supabase.rpc('update_assistant', {
        p_assistant_id: data.assistantId,
        p_role_name: data.roleName,
        p_permissions: data.permissions as any,
        p_is_active: data.isActive,
      });

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assistants', restaurantId] });
    },
  });
}

/**
 * Hook to update assistant password
 */
export function useUpdateAssistantPassword() {
  return useMutation({
    mutationFn: async (data: { assistantId: string; newPassword: string }) => {
      const { data: result, error } = await supabase.rpc('update_assistant_password', {
        p_assistant_id: data.assistantId,
        p_new_password: data.newPassword,
      });

      if (error) throw error;
      return result;
    },
  });
}

/**
 * Hook to delete (soft delete) an assistant
 */
export function useDeleteAssistant(restaurantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (assistantId: string) => {
      const { data, error } = await supabase.rpc('delete_assistant', {
        p_assistant_id: assistantId,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assistants', restaurantId] });
    },
  });
}

/**
 * Hook to check username availability across staff and assistants
 */
export function useCheckUsernameAvailability(restaurantId: string) {
  return useMutation({
    mutationFn: async (username: string) => {
      const { data, error } = await supabase.rpc('check_username_availability', {
        p_username: username,
        p_restaurant_id: restaurantId,
      });

      if (error) throw error;

      const result = data?.[0];
      return {
        available: result?.available || false,
        existsInStaff: result?.exists_in_staff || false,
        existsInAssistants: result?.exists_in_assistants || false,
      };
    },
  });
}
